import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CookieConsent } from "./components/shared/CookieConsent";
import { ScrollToTop } from "./components/shared/ScrollToTop";
import { UpdateBanner } from "./components/system/UpdateBanner";
import { Loader2 } from "lucide-react";

import { AdminRoute } from "./components/auth/AdminRouteGuard";
import { RequireVerifiedEmail, RequireSubscriber } from "./components/auth/AccessGuards";
import { GeoBlockGuard } from "./components/auth/GeoBlockGuard";
import { AdminErrorBoundary } from "./components/admin/AdminErrorBoundary";

import HomePage from "./pages/HomePage";
import NotFound from "./pages/NotFound";

const NewsPage = lazy(() => import("./pages/NewsPage"));
const ReviewHub2026Page = lazy(() => import("./pages/ReviewHub2026Page"));
const VehicleHubDetailPage = lazy(() => import("./pages/VehicleHubDetailPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const FeaturedPage = lazy(() => import("./pages/FeaturedPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AdvertisePage = lazy(() => import("./pages/AdvertisePage"));
const IndustryInsightsPage = lazy(() => import("./pages/IndustryInsightsPage"));
const SubscriberDashboardPage = lazy(() => import("./pages/SubscriberDashboardPage"));
const UpgradePage = lazy(() => import("./pages/UpgradePage"));

// Deal Builder (standalone calculator)
const DealBuilderPage = lazy(() => import("./pages/DealBuilderPage"));
const DealBuilderLandingPage = lazy(() => import("./pages/DealBuilderLandingPage"));
const DealDetailPage = lazy(() => import("./pages/DealDetailPage"));
const DealIQDemoPage = lazy(() => import("./pages/DealIQDemoPage"));

// Auth
const BuyerAuthPage = lazy(() => import("./pages/BuyerAuthPage"));
const VerificationSentPage = lazy(() => import("./pages/auth/VerificationSentPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));

// Admin
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminArticleEdit = lazy(() => import("./pages/admin/AdminArticleEdit"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminTaxonomy = lazy(() => import("./pages/admin/AdminTaxonomy"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminIntegrations = lazy(() => import("./pages/admin/AdminIntegrations"));
const AdminAdvertising = lazy(() => import("./pages/admin/AdminAdvertising"));
const AdminNewsSources = lazy(() => import("./pages/admin/AdminNewsSources"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminContactSubmissions = lazy(() => import("./pages/admin/AdminContactSubmissions"));
const AdminSmokeTest = lazy(() => import("./pages/admin/AdminSmokeTest"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminVehicleHubPage = lazy(() => import("./pages/admin/vehicle-hub/AdminVehicleHubPage"));
const AdminVehicleHubDraftsPage = lazy(() => import("./pages/admin/vehicle-hub/AdminVehicleHubDraftsPage"));
const AdminVehicleDetailPage = lazy(() => import("./pages/admin/vehicle-hub/AdminVehicleDetailPage"));
const AdminVehicleTestWorkflowPage = lazy(() => import("./pages/admin/vehicle-hub/AdminVehicleTestWorkflowPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const wrapAdmin = (el: JSX.Element) => (
  <AdminErrorBoundary><AdminRoute>{el}</AdminRoute></AdminErrorBoundary>
);

const App = () => (
  <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CookieConsent />
        <UpdateBanner />
        <GeoBlockGuard>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<ArticleDetailPage />} />
              <Route path="/reviews/2026" element={<ReviewHub2026Page />} />
              <Route path="/reviews/2026/:make/:model" element={<VehicleHubDetailPage />} />
              <Route path="/reviews/2026/:make/:model/:trim" element={<VehicleHubDetailPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:slug" element={<EventDetailPage />} />
              <Route path="/videos" element={<VideosPage />} />
              <Route path="/featured" element={<FeaturedPage />} />
              <Route path="/deals/builder" element={<DealBuilderLandingPage />} />
              <Route path="/deals/builder/create" element={<DealBuilderPage />} />
              <Route path="/deals/shared/:shareToken" element={<DealDetailPage />} />
              <Route path="/deal-iq-demo" element={<DealIQDemoPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/advertise" element={<AdvertisePage />} />
              <Route path="/insights" element={<IndustryInsightsPage />} />
              <Route path="/upgrade" element={<UpgradePage />} />
              <Route path="/insights/dashboard" element={
                <RequireSubscriber><SubscriberDashboardPage /></RequireSubscriber>
              } />

              {/* Auth */}
              <Route path="/auth/buyer" element={<BuyerAuthPage />} />
              <Route path="/auth/verification-sent" element={<VerificationSentPage />} />
              <Route path="/auth/verify" element={<VerifyEmailPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={wrapAdmin(<AdminDashboard />)} />
              <Route path="/admin/articles" element={wrapAdmin(<AdminArticles />)} />
              <Route path="/admin/articles/:id" element={wrapAdmin(<AdminArticleEdit />)} />
              <Route path="/admin/events" element={wrapAdmin(<AdminEvents />)} />
              <Route path="/admin/taxonomy" element={wrapAdmin(<AdminTaxonomy />)} />
              <Route path="/admin/subscribers" element={wrapAdmin(<AdminSubscribers />)} />
              <Route path="/admin/integrations" element={wrapAdmin(<AdminIntegrations />)} />
              <Route path="/admin/advertising" element={wrapAdmin(<AdminAdvertising />)} />
              <Route path="/admin/news-sources" element={wrapAdmin(<AdminNewsSources />)} />
              <Route path="/admin/audit-logs" element={wrapAdmin(<AdminAuditLogs />)} />
              <Route path="/admin/analytics" element={wrapAdmin(<AdminAnalytics />)} />
              <Route path="/admin/contact-submissions" element={wrapAdmin(<AdminContactSubmissions />)} />
              <Route path="/admin/ops/smoke" element={wrapAdmin(<AdminSmokeTest />)} />
              <Route path="/admin/subscriptions" element={wrapAdmin(<AdminSubscriptions />)} />
              <Route path="/admin/vehicle-hub" element={wrapAdmin(<AdminVehicleHubPage />)} />
              <Route path="/admin/vehicle-hub/review-drafts" element={wrapAdmin(<AdminVehicleHubDraftsPage />)} />
              <Route path="/admin/vehicle-hub/test-workflow" element={wrapAdmin(<AdminVehicleTestWorkflowPage />)} />
              <Route path="/admin/vehicle-hub/vehicles/:vehicle_model_id" element={wrapAdmin(<AdminVehicleDetailPage />)} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </GeoBlockGuard>
      </BrowserRouter>
    </TooltipProvider>
  </ThemeProvider>
);

export default App;
